import { useMemo } from "react";
import type { ReactNode } from "react";

import { getSchemaHookRegistry } from "./schemaRegistry";

/**
 * Generic hook to get custom cell renderers for any integration module.
 * Resolves to integration-specific hook based on integrationName and moduleName.
 * 
 * @param integrationName - The integration name (e.g., "indeed")
 * @param moduleName - The module name (e.g., "job-sync")
 * @param clientId - The client ID
 * @returns Custom cell renderers object, or undefined if not available
 */
export function useSchemaCellRenderers(
  integrationName: string | undefined,
  moduleName: string,
  clientId: string | undefined
): Record<string, (value: unknown, row: unknown) => ReactNode> | undefined {
  // Get module registry and resolve hook
  const moduleRegistry = getSchemaHookRegistry(moduleName);
  const registeredHook = moduleRegistry?.getCellRenderersHook?.(integrationName ?? "");
  
  // Create a no-op hook that returns undefined if no registered hook exists
  // This ensures hooks are always called unconditionally
  const noOpHook = () => undefined;
  const hookToCall = registeredHook ?? noOpHook;
  
  // Call the hook unconditionally (satisfies React hooks rules)
  const cellRenderers = hookToCall({ clientId });
  
  return useMemo(() => cellRenderers, [cellRenderers]);
}
